import { useEffect, useState } from "react";
import { Check, ChevronDown, FileEdit, Loader2, Play, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELD_OPTIONS = [
  { key: "education", label: "Educational explanation" },
  { key: "interpretation", label: "Interpretation note" },
  { key: "topic", label: "Topic title" },
];

const STATUS_STYLE = {
  pending: "bg-gold/15 text-gold-dark",
  approved: "bg-sage text-forest",
  rejected: "bg-charcoal/10 text-taupe",
};

export default function AdminAssessment() {
  const { member: me } = useAuth();
  const [content, setContent] = useState(null);
  const [openQ, setOpenQ] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const canApprove = (me?.permissions || []).includes("*");

  const loadProposals = () =>
    api.get("/admin/assessment/proposals").then(({ data }) => setProposals(data.proposals));

  useEffect(() => {
    document.title = "Assessment Content | Rooted Healing Admin";
    api.get("/admin/assessment/content").then(({ data }) => setContent(data));
    loadProposals().catch(() => setProposals([]));
  }, []);

  const runPreview = async (scenarioId) => {
    setRunning(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/assessment/preview", { scenario_id: scenarioId });
      setPreview({ scenarioId, results: data.results });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setRunning(false);
  };

  const submitProposal = async () => {
    if (!draft || draft.value.trim().length < 3) {
      toast.error("Write the proposed wording first.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/assessment/propose", {
        question_id: draft.questionId,
        field: draft.field,
        value: draft.value.trim(),
      });
      toast.success("Proposal saved. It goes live only after the Owner approves it.");
      setDraft(null);
      loadProposals();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const review = async (proposalId, action) => {
    try {
      await api.post(`/admin/assessment/proposals/${proposalId}/${action}`);
      toast.success(action === "approve" ? "Approved and published." : "Proposal rejected.");
      loadProposals();
      const { data } = await api.get("/admin/assessment/content");
      setContent(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!content) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="admin-assessment">
      <h1 className="font-serif text-3xl text-forest">Assessment Content</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        The approved question bank, scoring rules, and interpretation content. Question IDs,
        answer IDs, scoring thresholds, and classifications are locked. Educational wording can
        be refined through propose, review, approve, and publish, which never changes scoring
        or previously generated classifications.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
        <div className="rounded-2xl border border-forest/10 bg-white/75 p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-taupe">Questions</p>
          <p className="mt-1 font-serif text-3xl text-forest">{content.questions.length}</p>
        </div>
        <div className="rounded-2xl border border-forest/10 bg-white/75 p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-taupe">Patterns</p>
          <p className="mt-1 font-serif text-3xl text-forest">{content.patterns.length}</p>
        </div>
        <div className="rounded-2xl border border-forest/10 bg-white/75 p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-taupe">Scoring version</p>
          <p className="mt-1 font-serif text-lg text-forest">{content.scoring_version}</p>
        </div>
        <div className="rounded-2xl border border-forest/10 bg-white/75 p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-taupe">Content version</p>
          <p className="mt-1 font-serif text-lg text-forest" data-testid="content-version">
            {content.content_version}
          </p>
        </div>
        <div className="rounded-2xl border border-forest/10 bg-white/75 p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-taupe">Status</p>
          <p className="mt-1 text-sm font-semibold text-gold-dark">{content.approval_status}</p>
        </div>
      </div>

      {proposals && proposals.length > 0 && (
        <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-7" data-testid="proposals-section">
          <h2 className="font-serif text-xl text-forest">Editorial proposals</h2>
          <p className="mt-1 text-xs text-taupe">
            Pending changes publish only after Owner approval. Approving never alters scores or
            historical results.
          </p>
          <div className="mt-4 flex flex-col gap-3">
            {proposals.map((p) => (
              <div
                key={p.proposal_id}
                data-testid={`proposal-${p.proposal_id}`}
                className="rounded-2xl border border-forest/10 p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-forest">
                    {p.question_id} · {FIELD_OPTIONS.find((f) => f.key === p.field)?.label || p.field}
                  </p>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[p.status]}`}>
                    {p.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-taupe">{p.question_text}</p>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-xl bg-charcoal/5 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-taupe">Current</p>
                    <p className="mt-1 text-xs leading-relaxed text-charcoal/70">{p.current_value || "none set"}</p>
                  </div>
                  <div className="rounded-xl bg-sage/60 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gold-dark">Proposed</p>
                    <p className="mt-1 text-xs leading-relaxed text-charcoal/80">{p.value}</p>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-taupe">
                  Proposed by {p.proposed_by} · {new Date(p.proposed_at).toLocaleString("en-US")}
                  {p.reviewed_by && ` · reviewed by ${p.reviewed_by}`}
                </p>
                {p.status === "pending" && canApprove && (
                  <div className="mt-3 flex gap-2">
                    <button
                      data-testid={`proposal-approve-${p.proposal_id}`}
                      onClick={() => review(p.proposal_id, "approve")}
                      className="inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-ivory hover:bg-forest-deep"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Approve & publish
                    </button>
                    <button
                      data-testid={`proposal-reject-${p.proposal_id}`}
                      onClick={() => review(p.proposal_id, "reject")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-900/25 px-4 py-2 text-xs font-semibold text-red-900/70 hover:border-red-900/50"
                    >
                      <X className="h-3.5 w-3.5" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
        <h2 className="font-serif text-xl text-forest">Result preview playground</h2>
        <p className="mt-2 text-sm text-taupe">
          Run the approved test scenarios through the engine to see exactly what a member would
          receive. Nothing is saved.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {content.test_scenarios.map((t) => (
            <button
              key={t.id}
              data-testid={`preview-scenario-${t.id.toLowerCase()}`}
              onClick={() => runPreview(t.id)}
              disabled={running}
              className="inline-flex items-center gap-2 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-gold disabled:opacity-50"
            >
              <Play className="h-3 w-3" />
              {t.id}: {t.name}
            </button>
          ))}
        </div>
        {running && <Loader2 className="mt-5 h-6 w-6 animate-spin text-forest" />}
        {preview && (
          <div className="mt-6 rounded-2xl bg-sage/50 p-6" data-testid="preview-result">
            <p className="font-serif text-lg text-forest">
              {preview.scenarioId} → Overall: {preview.results.overall}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {Object.entries(preview.results.dimensions).map(([cat, d]) => (
                <p key={cat} className="text-sm text-charcoal/75">
                  <span className="font-semibold">{cat}:</span> {d.label}
                </p>
              ))}
            </div>
            <p className="mt-3 text-sm text-charcoal/70">
              Patterns: {preview.results.patterns.join(", ") || "none"}
            </p>
            {preview.results.overall_narrative?.map((p, i) => (
              <p key={i} className="mt-2 text-sm leading-relaxed text-charcoal/70">
                {p}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
        <h2 className="font-serif text-xl text-forest">Scoring and classification rules</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-2xl bg-sage/50 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
              Dimension rules
            </p>
            <p className="mt-2 text-sm leading-relaxed text-charcoal/75">
              {content.classification.dimension_rules}
            </p>
            <p className="mt-2 text-xs text-taupe">{content.classification.minimum_scored_responses}</p>
          </div>
          <div className="rounded-2xl bg-sage/50 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
              Overall rules
            </p>
            <p className="mt-2 text-sm leading-relaxed text-charcoal/75">
              {content.classification.overall_rules}
            </p>
          </div>
        </div>
        <p className="mt-4 text-xs text-taupe">
          Labels: {content.classification_labels.join(" · ")} · Numbers, where shown, are a
          self-reported support index: {content.classification.numbers}
        </p>
      </div>

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
        <h2 className="font-serif text-xl text-forest">Question bank</h2>
        <div className="mt-4 flex flex-col">
          {content.questions.map((q) => (
            <div key={q.id} className="border-b border-forest/8">
              <button
                data-testid={`question-toggle-${q.id.toLowerCase()}`}
                onClick={() => setOpenQ(openQ === q.id ? null : q.id)}
                className="flex w-full items-center justify-between gap-4 py-4 text-left"
              >
                <span className="text-sm">
                  <span className="font-mono text-xs font-bold text-gold-dark">{q.id}</span>{" "}
                  <span className="font-semibold text-forest">{q.text}</span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-taupe transition-transform ${openQ === q.id ? "rotate-180" : ""}`}
                />
              </button>
              {openQ === q.id && (
                <div className="pb-5 pl-2">
                  <ul className="flex flex-col gap-1.5">
                    {q.answers.map((a) => (
                      <li key={a.id} className="text-xs text-charcoal/75">
                        <span className="font-mono text-gold-dark">{a.letter}.</span> {a.text}{" "}
                        <span className="text-taupe">
                          ({a.mode}, {a.points === null ? "no points" : `${a.points} pts`})
                        </span>
                      </li>
                    ))}
                  </ul>
                  {q.source_notes?.["What this explores"] && (
                    <p className="mt-3 text-xs italic text-taupe">
                      Explores: {q.source_notes["What this explores"]}
                    </p>
                  )}
                  {q.source_notes?.["Educational response"] && (
                    <p className="mt-2 text-xs leading-relaxed text-charcoal/70">
                      <span className="font-semibold text-forest">Educational note: </span>
                      {content.active_overrides?.[q.id]?.education || q.source_notes["Educational response"]}
                      {content.active_overrides?.[q.id]?.education && (
                        <span className="ml-2 rounded-full bg-sage px-2 py-0.5 text-[10px] font-bold text-forest">
                          edited
                        </span>
                      )}
                    </p>
                  )}
                  {draft?.questionId === q.id ? (
                    <div className="mt-4 rounded-2xl border border-gold/30 bg-white/70 p-4">
                      <select
                        data-testid="proposal-field"
                        value={draft.field}
                        onChange={(e) => setDraft((d) => ({ ...d, field: e.target.value }))}
                        className="w-full rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs outline-none focus:border-gold"
                      >
                        {FIELD_OPTIONS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      <textarea
                        data-testid="proposal-value"
                        rows={3}
                        maxLength={2000}
                        placeholder="Proposed wording (goes live only after Owner approval)"
                        value={draft.value}
                        onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                        className="mt-3 w-full rounded-2xl border border-forest/20 bg-white/70 px-4 py-3 text-sm outline-none focus:border-gold"
                      />
                      <div className="mt-3 flex gap-2">
                        <button
                          data-testid="proposal-submit"
                          onClick={submitProposal}
                          disabled={saving}
                          className="inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-ivory hover:bg-forest-deep disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Submit for approval
                        </button>
                        <button
                          onClick={() => setDraft(null)}
                          className="text-xs font-semibold text-taupe underline-offset-4 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid={`propose-edit-${q.id.toLowerCase()}`}
                      onClick={() => setDraft({ questionId: q.id, field: "education", value: "" })}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-gold"
                    >
                      <FileEdit className="h-3.5 w-3.5" />
                      Propose an educational wording edit
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
